import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Shield, CheckCircle, Loader2, XCircle } from 'lucide-react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';

export default function ProtectedPurchaseStatusPage() {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attempts, setAttempts] = useState(0);

  const isSuccess = searchParams.get('status') === 'success';
  const formatPrice = (p) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(p);

  useEffect(() => {
    if (!sessionId || !isSuccess) {
      setLoading(false);
      return;
    }
    pollStatus();
  }, [sessionId]);

  const pollStatus = async () => {
    try {
      const data = await api.getProtectedPurchaseStatus(sessionId);
      setStatus(data);
      if (data.payment_status === 'paid' || data.status === 'paid') {
        setLoading(false);
        return;
      }
      if (attempts < 10) {
        setTimeout(() => {
          setAttempts(a => a + 1);
          pollStatus();
        }, 2000);
      } else {
        setLoading(false);
      }
    } catch {
      setLoading(false);
    }
  };

  if (!isSuccess) {
    return (
      <div className="min-h-screen bg-[#0A0A0C] flex items-center justify-center p-6" data-testid="purchase-canceled">
        <div className="glass-card p-8 text-center max-w-md">
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-medium text-zinc-100 mb-2">Pago cancelado</h1>
          <p className="text-sm text-zinc-400 mb-6">Tu pago fue cancelado. No se realizó ningún cargo.</p>
          <Link to="/explorar">
            <Button className="bg-zinc-100 text-zinc-900 hover:bg-white">Volver al marketplace</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0C] flex items-center justify-center p-6" data-testid="purchase-status-page">
      <div className="glass-card p-8 text-center max-w-md">
        {loading ? (
          <div className="space-y-4">
            <Loader2 className="w-16 h-16 text-purple-400 animate-spin mx-auto" />
            <h1 className="text-2xl font-medium text-zinc-100">Verificando pago...</h1>
            <p className="text-sm text-zinc-400">Estamos confirmando tu pago con Stripe. Esto toma unos segundos.</p>
          </div>
        ) : status?.status === 'paid' || status?.payment_status === 'paid' ? (
          <div className="space-y-4">
            <div className="w-20 h-20 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto">
              <Shield className="w-10 h-10 text-emerald-400" />
            </div>
            <h1 className="text-2xl font-medium text-zinc-100">Compra Protegida Exitosa</h1>
            <p className="text-sm text-zinc-400 mb-4">
              Tu pago fue recibido y los fondos están en custodia de MorphMX hasta que confirmes la recepción.
            </p>
            {status && (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between p-2 bg-white/[0.02] rounded">
                  <span className="text-zinc-500">Artículo</span>
                  <span className="text-zinc-200">{status.title}</span>
                </div>
                <div className="flex justify-between p-2 bg-white/[0.02] rounded">
                  <span className="text-zinc-500">Total pagado</span>
                  <span className="text-zinc-200">{formatPrice(status.amount)}</span>
                </div>
                <div className="flex justify-between p-2 bg-white/[0.02] rounded">
                  <span className="text-zinc-500">Estado</span>
                  <span className="text-emerald-400 flex items-center gap-1">
                    <CheckCircle className="w-3.5 h-3.5" /> Fondos en custodia
                  </span>
                </div>
              </div>
            )}
            <Link to="/dashboard/pagos">
              <Button className="bg-purple-600 hover:bg-purple-500 text-white mt-4">
                <Shield className="w-4 h-4 mr-2" /> Ver mis compras protegidas
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            <XCircle className="w-16 h-16 text-amber-400 mx-auto" />
            <h1 className="text-2xl font-medium text-zinc-100">Verificación en proceso</h1>
            <p className="text-sm text-zinc-400">
              Tu pago está siendo procesado. Revisa el estado en tu dashboard.
            </p>
            <Link to="/dashboard/pagos">
              <Button className="bg-zinc-100 text-zinc-900 hover:bg-white">Ir a mi dashboard</Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
